import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";

import { ExpandedMessageObject } from "@data-types/chat";
import useFileHandler from "@features/chat/shared/useFileHandler";
import { Button } from "@components/buttons";
import { ChatNameForm, UploadedFilesPreview } from "@features/chat/shared";
import { ChatViewStore } from "@state/stores/chat/ChatViewStore";
import { DiagnosticsProvider } from "@state/providers/diagnosticsProvider";
import { MainHtmlElementContext } from "@state/context/main-html/MainHtmlElementContext";
import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";

import MessageContainer from "../MessageContainer";

export default observer(function SupportChatArea({
	chatViewStore,
	diagnosticsProvider,
}: {
	chatViewStore: ChatViewStore;
	diagnosticsProvider: DiagnosticsProvider;
}) {
	const { chatChannelHandler } = chatViewStore;

	const messages = chatChannelHandler.supportChannelMessageList;
	const pagination = chatChannelHandler.supportMessagePaginationObject;
	const mainElement = useContext(MainHtmlElementContext);

	const chatContainerRef = useRef<HTMLDivElement>(null);
	const scrollingDivRef = useRef<HTMLDivElement>(null);
	const messageRef = useRef<HTMLDivElement>(null);

	const { t } = useTranslation();

	const [scrollPosition, setScrollPosition] = useState(0);

	useEffect(() => {
		if (mainElement) {
			messages && messages.totalMessages <= 30 && scrollToBottom();
		}
	}, [messages?.totalMessages]);

	useEffect(() => {
		if (
			chatViewStore.imagesDoneLoading &&
			messages &&
			messages.totalMessages <= 30
		) {
			scrollToBottom();
			chatViewStore.setImagesDoneLoading(false);
		}
	}, [chatViewStore.imagesDoneLoading]);

	useEffect(() => {
		if (mainElement != null) {
			mainElement.addEventListener("scroll", onScrollHandler);

			return () => {
				mainElement?.removeEventListener("scroll", onScrollHandler);
			};
		}
	}, [mainElement]);

	const onScrollHandler = () => {
		if (mainElement) {
			const { scrollTop } = mainElement;
			setScrollPosition(scrollTop);

			const pageObject =
				chatChannelHandler.supportMessagePaginationObject;

			if (scrollTop == 0) {
				pageObject?.onNextPage && pageObject.onNextPage();
			}
		}
	};

	const showBackToBottomBtn = useMemo(() => {
		if (scrollingDivRef && scrollingDivRef.current) {
			const currentHeight = scrollingDivRef.current.clientHeight;
			const backToBottomArea = currentHeight - 1000;
			return scrollPosition <= backToBottomArea;
		}
	}, [scrollPosition, scrollingDivRef.current?.clientHeight]);

	function scrollToBottom(behavior?: ScrollBehavior) {
		if (mainElement) {
			if (scrollingDivRef && scrollingDivRef.current) {
				mainElement.scrollTo({
					top: scrollingDivRef.current.clientHeight,
					behavior,
				});
			}
		}
	}

	if (!chatViewStore.chatDisplayName) {
		return <ChatNameForm store={chatViewStore} />;
	}

	if (messages == null && !chatChannelHandler.firstVisit) {
		return null;
	}

	const isChannelEmpty = (messages?.totalMessages || 0) < 1;

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Mobile/SupportChatArea.tsx"}
		>
			<div
				ref={chatContainerRef}
				id="support-scrollableDiv"
				className="chat--support__box"
			>
				<div
					ref={scrollingDivRef}
					className={
						"chat__scroll" +
						(!messages || messages.totalMessages < 1
							? " chat--support__empty"
							: "")
					}
				>
					<div id="support-bottom-scroll-div" />

					{!messages || messages.totalMessages < 1 ? (
						<span className="chat__no-more">
							{t("CHAT.NO_MESSAGES")}
						</span>
					) : (
						<>
							{Object.keys(messages.groupedMessages).map(
								(date) => (
									<React.Fragment key={date}>
										{messages.groupedMessages[date].map(
											(
												message: ExpandedMessageObject,
												index: number
											) => (
												<div
													key={index}
													ref={messageRef}
												>
													<MessageContainer
														noReport
														noReply
														support
														message={message}
													/>
												</div>
											)
										)}
										<div className="chat--support__date">
											<div className="chat--support__date__label">
												{date}
											</div>
										</div>
									</React.Fragment>
								)
							)}
							{pagination &&
								!pagination.hasNextPage &&
								messages.totalMessages > 30 && (
									<span className="chat__no-more">
										{t("CHAT.NO_MORE_MESSAGES_AVAILABLE")}
									</span>
								)}
						</>
					)}
				</div>
			</div>

			<ChatMessageForm
				showInfo={isChannelEmpty || messages?.showAdditionalInfo}
				store={chatViewStore}
				diagnostics={diagnosticsProvider}
				scrollToBottom={scrollToBottom}
			/>

			{showBackToBottomBtn && (
				<button
					type="button"
					className="chat__btn--scroll chat--support__scroll btn btn--round btn--round--sml btn--icon btn--tertiary"
					onClick={() => scrollToBottom("smooth")}
				>
					<i className="u-icon u-icon--tny u-icon--arrow--down" />
				</button>
			)}
		</ErrorBoundaryWithPropagation>
	);
});

const ChatMessageForm = observer(function ChatMessageForm({
	scrollToBottom,
	store,
	diagnostics,
	showInfo,
}: {
	scrollToBottom: () => void;
	store: ChatViewStore;
	diagnostics: DiagnosticsProvider;
	showInfo?: boolean;
}) {
	const { t } = useTranslation();
	const { chatChannelHandler } = store;

	const inputRef = useRef<HTMLInputElement>(null);

	const { chooseFile, files, removeFile, setFiles } =
		useFileHandler(inputRef);
	const [inputValue, setInputValue] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isTextareaFocused, setIsTextareaFocused] = useState(false);

	const sendMsg = async (
		e:
			| React.FormEvent<HTMLFormElement>
			| React.KeyboardEvent<HTMLTextAreaElement>
	) => {
		setIsSubmitting(true);

		e.preventDefault();

		await chatChannelHandler.sendSupportMsg(
			inputValue,
			files[0],
			diagnostics?.errorMessages
		);
		setFiles([]);
		setInputValue("");
		diagnostics?.onDispose();
		setIsSubmitting(false);

		scrollToBottom();
	};

	return (
		<form onSubmit={sendMsg} className="chat__footer chat--support__footer">
			{!isTextareaFocused && showInfo && (
				<InfoMessageComponent
					type="default"
					size="sml"
					addClassName="chat--support__notice"
					customIcon="u-icon--med u-icon--support--alt"
					message={t("CHAT.SUPPORT.ADDITIONAL_INFO")}
				/>
			)}

			{files && files.length > 0 && (
				<UploadedFilesPreview removeFile={removeFile} files={files} />
			)}

			<div className="chat__textbox chat--support__textbox">
				<textarea
					maxLength={10000}
					id="chat-textarea"
					rows={3}
					value={inputValue}
					onFocus={() => {
						setIsTextareaFocused(true);
					}}
					className="chat__textarea chat--support__textarea input input--base input--textarea"
					placeholder={t("CHAT.ENTER_MESSAGE")}
					onChange={(e) => setInputValue(e.target.value)}
				></textarea>

				<label
					htmlFor="files"
					className="chat__file btn btn--square btn--square--sml btn--ghost u-mar--right--xtny"
				>
					<i className="u-icon u-icon--sml u-icon--attachment u-color--text--secondary" />
				</label>
				<input
					ref={inputRef}
					name="files"
					id="files"
					accept="image/*, video/*"
					type="file"
					onChange={chooseFile}
					className="u-display--none"
					disabled={files.length > 0}
				/>

				<Button
					id="send_message_button"
					className="chat__send btn--square btn--square--sml btn--accent"
					iconOnly
					iconClassName="u-icon--sml u-icon--arrow--send u-color--accent--text"
					disabled={
						(inputValue.length < 1 && files.length < 1) ||
						isSubmitting
					}
				/>
			</div>
		</form>
	);
});
